import React, {
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from "react";

import { FAStringInput } from "@impero-com/common/components/form/formAtomFields/faStringInput";
import { formAtom } from "@impero-com/common/utils/form/formAtom";
import {
    useFormAtom,
    useCompoundFormState,
} from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { t } from "@impero-com/common/utils/i18n";
import { Button, FieldError, FormLabel, Svg } from "@impero-com/ui";
import { Atom, Getter, PrimitiveAtom, useAtom, useAtomValue } from "jotai";
import { focusAtom } from "jotai-optics";
import * as Yup from "yup";

import DeleteIcon from "img/delete.svg";
import { CONSTANTS } from "utils/constants";

import { CompoundFormForwardedRef } from "./fieldBuilder";
import { InnerBaseAtomsAtom, splitFormAtomWithoutKey } from "./hooks";
import * as theme from "./theme.css";

interface FieldOptionsEditorProps<FT extends { options: string[] }> {
    atom: FormAtom<FT>;
    disabled?: boolean;
}

function useFieldTypeAtomOptions<FT extends { options: string[] }>(
    fieldTypeAtom: FormAtom<FT>
) {
    const [revalidate, setRevalidate] = useState<string | undefined>(undefined);

    const atoms = useMemo(() => {
        const optionsAtom: FormAtom<string[]> = formAtom(
            focusAtom(fieldTypeAtom, (fieldType) => fieldType.prop("options")),
            Yup.array()
                .defined()
                .min(1, t("FieldBuilder_Error_Options_OneRequired"))
        );

        const isOptionUnique = (
            atom: PrimitiveAtom<string>,
            newValue: string,
            optionsBaseAtomsAtom: InnerBaseAtomsAtom<string>,
            baseAtomsToFormAtoms: WeakMap<Atom<string>, FormAtom<string>>,
            get: Getter
        ) => {
            for (const a of get(optionsBaseAtomsAtom)) {
                if (a !== atom) {
                    // Get the non validated values to allow easy swapping of options.
                    const fa = baseAtomsToFormAtoms.get(a)!;
                    const aValue = get(fa);
                    if (aValue === newValue) {
                        return false;
                    }
                }
            }
            return true;
        };

        const {
            formAtomsAtom: optionsAtomsAtom,
            baseAtomsAtom: optionsBaseAtomsAtom,
        } = splitFormAtomWithoutKey(
            optionsAtom,
            (optionAtom, baseAtomsAtom, baseAtomsToFormAtoms, get) =>
                Yup.string()
                    .defined()
                    .max(CONSTANTS.FIELD_CONSTANTS.MAX_OPTION_LENGTH)
                    .required(t("FieldBuilder_Error_Option_Name_Required"))
                    .test({
                        name: "unique",
                        test: (value, { createError }) => {
                            const PATH = "options";
                            if (
                                !isOptionUnique(
                                    optionAtom,
                                    value,
                                    baseAtomsAtom,
                                    baseAtomsToFormAtoms,
                                    get
                                )
                            ) {
                                return createError({
                                    path: PATH,
                                    message: t(
                                        "FieldBuilder_Error_OptionAlreadyExists"
                                    ),
                                });
                            } else {
                                return true;
                            }
                        },
                    }),
            setRevalidate
        );
        return {
            optionsAtom,
            optionsAtomsAtom,
            optionsBaseAtomsAtom,
        };
    }, [fieldTypeAtom]);
    return {
        revalidate,
        setRevalidate,
        ...atoms,
    };
}

const FieldOptionsEditorInner = <FT extends { options: string[] }>(
    { atom: fieldTypeAtom, disabled = false }: FieldOptionsEditorProps<FT>,
    ref: CompoundFormForwardedRef
) => {
    const { revalidate, optionsAtom, optionsAtomsAtom, optionsBaseAtomsAtom } =
        useFieldTypeAtomOptions(fieldTypeAtom);

    const { error: optionsError } = useFormAtom(optionsAtom);
    const optionAtoms = useAtomValue(optionsAtomsAtom);
    const [baseAtoms, setBaseAtoms] = useAtom(optionsBaseAtomsAtom);

    const allAtoms = useMemo(() => {
        return {
            optionsAtom,
            optionsAtomsAtom,
        };
    }, [optionsAtom, optionsAtomsAtom]);
    const { validate: validateForm } = useCompoundFormState(
        allAtoms,
        optionsAtom
    );
    useImperativeHandle(ref, () => {
        return {
            validate(force) {
                return validateForm(force);
            },
        };
    }, [validateForm]);

    useEffect(() => {
        validateForm(true);
    }, [revalidate, validateForm]);

    const styleVariant = disabled ? "borderless" : "normal";

    return (
        <>
            <FormLabel>{t("FieldBuilder_Drawer_Field_Options")}</FormLabel>
            <div className={theme.fieldOptionsTable}>
                {optionAtoms.map((atom, index) => {
                    return (
                        <div
                            key={atom.toString()}
                            className={theme.fieldOptionRow}
                        >
                            <div className={theme.fieldOptionValue}>
                                <FAStringInput
                                    atom={atom}
                                    disabled={disabled}
                                    styleVariant={styleVariant}
                                />
                            </div>
                            {!disabled && (
                                <div>
                                    <Button
                                        id={`delete-option-link-${index}`}
                                        variant="link"
                                        className={theme.deleteTableRowButton}
                                        onClick={() =>
                                            setBaseAtoms({
                                                type: "remove",
                                                atom: baseAtoms[index],
                                            })
                                        }
                                    >
                                        <Svg
                                            src={DeleteIcon}
                                            className={theme.deleteIcon}
                                        />
                                    </Button>
                                </div>
                            )}
                        </div>
                    );
                })}
                <FieldError error={optionsError?.value} />
            </div>
            {!disabled && (
                <div className={theme.fieldOptionAddRow}>
                    <Button
                        id="add-option"
                        variant="dashed"
                        className={theme.fieldOptionAdd}
                        onClick={() =>
                            setBaseAtoms({ type: "insert", value: "" })
                        }
                    >
                        {t("FieldBuilder_Drawer_Field_Option_Add")}
                    </Button>
                    <div className={theme.tableAddPadder} />
                </div>
            )}
        </>
    );
};

export const FieldOptionsEditor = React.forwardRef(FieldOptionsEditorInner);
